import { useCallback, useEffect, useState } from "react";
import { api } from "./lib/api";
import { CartProvider } from "./context/CartContext";
import { ToastProvider } from "./context/ToastContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthModal from "./components/AuthModal";

import Header from "./components/Header";
import Hero from "./components/Hero";
import DisclaimerStrip from "./components/DisclaimerStrip";
import ConcernExplorer from "./components/ConcernExplorer";
import ConcernModal from "./components/ConcernModal";
import Catalogue from "./components/Catalogue";
import Packages from "./components/Packages";
import About from "./components/About";
import Gallery from "./components/Gallery";
import Testimonials from "./components/Testimonials";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import CheckoutModal from "./components/CheckoutModal";
import FloatWhatsApp from "./components/FloatWhatsApp";

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <CartProvider>
          <Shop />
        </CartProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

function Shop() {
  const { isAuthenticated, isConfirmed } = useAuth();
  const [data, setData] = useState(null);
  const [concern, setConcern] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  // When true, a successful auth/verify should flow straight into checkout.
  const [authThenCheckout, setAuthThenCheckout] = useState(false);

  useEffect(() => {
    Promise.all([
      api.settings(),
      api.concerns(),
      api.packages(),
      api.testimonials(),
      api.gallery(),
      api.tags(),
    ]).then(([settings, concerns, packages, testimonials, gallery, tags]) => {
      setData({
        settings: settings.site_settings,
        concerns: concerns.concerns,
        packages: packages.packages,
        testimonials: testimonials.testimonials,
        gallery: gallery.gallery,
        tags: tags.tags,
      });
    });
  }, []);

  const openConcern = useCallback((slug) => {
    api.concern(slug).then((d) => setConcern(d.concern));
  }, []);

  const enquirePackage = useCallback(
    (pkgName) => {
      if (!data) return;
      const msg = `Hello! 🌿 I'm interested in the *${pkgName}*. Could you tell me what it includes, the price, and how to order?`;
      window.open(`https://wa.me/${data.settings.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
    },
    [data]
  );

  // Checkout requires a verified account (Jumia-style): sign in / verify first.
  const startCheckout = useCallback(() => {
    setCartOpen(false);
    if (isAuthenticated && isConfirmed) {
      setCheckoutOpen(true);
    } else {
      setAuthThenCheckout(true);
      setAuthMode(isAuthenticated ? "verify" : "login"); // logged-in but unverified -> verify step
      setAuthOpen(true);
    }
  }, [isAuthenticated, isConfirmed]);

  const openLogin = useCallback(() => {
    setAuthThenCheckout(false);
    setAuthMode("login");
    setAuthOpen(true);
  }, []);

  const onAuthSuccess = useCallback(() => {
    setAuthOpen(false);
    if (authThenCheckout) {
      setAuthThenCheckout(false);
      setCheckoutOpen(true);
    }
  }, [authThenCheckout]);

  if (!data) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "70vh", color: "var(--muted)" }}>
        <div style={{ textAlign: "center" }}>
          <i className="fa-solid fa-seedling" style={{ fontSize: "2rem", color: "var(--leaf)" }} />
          <p style={{ marginTop: "12px" }}>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header onCartClick={() => setCartOpen(true)} onLoginClick={openLogin} />
      <Hero onConcern={openConcern} />
      <DisclaimerStrip />
      <ConcernExplorer concerns={data.concerns} onConcern={openConcern} />
      <Catalogue tags={data.tags} onConcern={openConcern} onEnquirePackage={enquirePackage} />
      <Packages packages={data.packages} onEnquire={enquirePackage} />
      <About />
      <Gallery items={data.gallery} />
      <Testimonials testimonials={data.testimonials} />
      <Contact settings={data.settings} />
      <Footer settings={data.settings} />

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={startCheckout} />
      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />

      <AuthModal
        open={authOpen}
        initialMode={authMode}
        onClose={() => { setAuthOpen(false); setAuthThenCheckout(false); }}
        onSuccess={onAuthSuccess}
        message={authThenCheckout ? "Sign in or create an account to complete your order." : undefined}
      />

      {concern && (
        <ConcernModal concern={concern} onClose={() => setConcern(null)} onEnquirePackage={enquirePackage} />
      )}

      <FloatWhatsApp settings={data.settings} />
    </>
  );
}
